// Itinerary related models

import { IGuest, ITravelRequest, TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { ItineraryActivity } from "./activity.model";
import { ItineraryDay } from "./itinerary-day.model";
import { dateToLocalString } from "@/components/utils/dateUtils";

export type ItineraryStatus = 'draft' | 'pending' | 'approved' | 'completed' | 'rejected' | 'sended' | 'expired' | 'request_changes';

export type ItineraryTotals = {
  commisionable_total: number;
  not_commisionable_total: number;
  p2b_commission_percentage: number;
  td_fee_percentage: number;
  custom_fee: number;             // Sovrapprezzo admin
  // Calculated fields (not stored in DB)
  p2b_markup_gross: number;       // Fee advisor (lordo)
  vat_amount: number;             // Deprecato (compatibilità)
  p2b_markup_net: number;         // Deprecato (compatibilità)
  td_fee_amount: number;          // Guadagno Travel Designer (Fee - Place2Be fee)
  p2b_fee_amount: number;         // Place2Be fee (3% del totale con fee)
  total: number;                  // Totale cliente
}

export interface IItineraryData {
  id?: string;
  title: string;
  start_date: string;
  end_date: string;
  guests_data: IGuest[];
  budget: number;
  description: string;
  travel_request_id?: string;
  image?: string | null;
  custom_fee?: number;
  itinerary_days?: ItineraryDay[];
  status?: ItineraryStatus;
  note?: string;

  refundable?: 'full' | 'partial' | 'non_refundable' | null;
  commisionable_total?: number,
  not_commisionable_total?: number,
  p2b_commission_percentage: number,
  p2b_commission_type?: 'percentage' | 'fixed',
  p2b_commission_fixed_amount?: number,
  p2b_markup_gross?: number,
  vat_amount?: number,
  p2b_markup_net?: number,
  p2b_commission_amount?: number,
  td_fee_percentage: number,
  td_fee_amount?: number,
  p2b_fee_amount?: number,
  total?: number,
  cancellation_policy_notes?: string | null,
}

export class ItineraryData implements IItineraryData {
  id?: string;
  title: string;
  start_date: string;
  end_date: string;
  guests_data: IGuest[];
  budget: number;
  description: string;
  image: string | null;
  status?: ItineraryStatus;
  travel_request_id?: string;
  travel_request?: TravelRequest;
  itinerary_days: ItineraryDay[] = [];
  note: string;

  refundable: 'full' | 'partial' | 'non_refundable' | null;
  custom_fee: number;
  commisionable_total?: number;
  not_commisionable_total?: number;
  p2b_commission_percentage: number;
  p2b_commission_type: 'percentage' | 'fixed';
  p2b_commission_fixed_amount: number;
  p2b_markup_gross?: number;
  vat_amount?: number;
  p2b_markup_net?: number;
  p2b_commission_amount?: number;
  td_fee_percentage: number;
  td_fee_amount?: number;
  p2b_fee_amount?: number;
  total?: number;
  cancellation_policy_notes?: string | null;

  get total_guests(): number {
    return this.guests_data.length || 1;
  }

  get firstDay(): string {
    return this.itinerary_days[0]?.date || this.start_date || '';
  }

  get hasTravelData(): boolean {
    return !!(
      this.title &&
      this.start_date &&
      this.end_date &&
      this.total_guests
    );
  };

  constructor(data?: IItineraryData) {
    this.id = data?.id || crypto.randomUUID();
    this.title = data?.title || '';
    this.image = data?.image || null;
    this.start_date = data?.start_date || '';
    this.end_date = data?.end_date || '';
    this.guests_data = data?.guests_data || [];
    this.budget = data?.budget || 0;
    this.description = data?.description || '';
    this.itinerary_days = data?.itinerary_days?.map(day => new ItineraryDay(day)) || [];
    this.travel_request_id = data?.travel_request_id || '';
    this.status = data?.status || 'draft';
    this.note = data?.note;


    this.refundable = data?.refundable ?? null;
    this.custom_fee = data?.custom_fee || 0;
    this.commisionable_total = data?.commisionable_total || 0;
    this.not_commisionable_total = data?.not_commisionable_total || 0;
    this.p2b_commission_percentage = data?.p2b_commission_percentage || 20;
    this.p2b_commission_type = data?.p2b_commission_type || 'percentage';
    this.p2b_commission_fixed_amount = data?.p2b_commission_fixed_amount || 0;
    this.p2b_commission_amount = data?.p2b_commission_amount || 0;
    this.td_fee_percentage = data?.td_fee_percentage || 97;
    this.td_fee_amount = data?.td_fee_amount || 0;
    this.total = data?.total || 0;
    this.cancellation_policy_notes = data?.cancellation_policy_notes ?? null;
  }

  fetchTravelRequest(data: ITravelRequest) {
    this.travel_request = new TravelRequest(data);
    this.travel_request_id = data.id || '';
    this.title = data.destinations?.join(', ') || '';
    this.start_date = data.travel_start_date || '';
    this.end_date = data.travel_end_date || '';
    this.guests_data = data.guests_data as IGuest[];
    this.budget = data.budget_max || 0;
    this.updateItineraryDays(this.start_date, this.end_date);
  }

  update(data: Partial<IItineraryData>): ItineraryData {
    const newData = new ItineraryData({
      ...this as IItineraryData,
      ...data,
    });
    // Non aggiorniamo più automaticamente i totali per preservare i valori manuali
    newData.travel_request = this.travel_request;
    return newData;
  }

  setItineraryDays(activities: ItineraryActivity[]) {
    this.itinerary_days = generateDaysFromActivities(activities);
    return this.update({ itinerary_days: this.itinerary_days });
  }

  updateItineraryDays(start_date: string, end_date: string) {
    this.itinerary_days = mergeDays(generateDaysFromDates(start_date, end_date), this.itinerary_days);
    return this.update({ itinerary_days: this.itinerary_days });
  }

  setItineraryNote(note: string) {
    return this.update({ note });
  }

  getPayload() {
    const totals = this.extractTotals();
    return {
      id: this.id,
      title: this.title,
      start_date: this.start_date,
      end_date: this.end_date,
      guests_data: this.guests_data,
      budget: this.budget,
      description: this.description,
      image: this.image,
      travel_request_id: this.travel_request_id,
      status: this.status,
      note: this.note,
      refundable: this.refundable ?? null,
      // Solo i campi che esistono nella tabella del database
      commisionable_total: totals.commisionable_total,
      not_commisionable_total: totals.not_commisionable_total,
      p2b_commission_percentage: totals.p2b_commission_percentage,
      p2b_commission_amount: totals.p2b_markup_gross, // markup lordo è il commission_amount
      p2b_commission_type: this.p2b_commission_type,
      p2b_commission_fixed_amount: this.p2b_commission_fixed_amount,
      td_fee_percentage: totals.td_fee_percentage,
      custom_fee: totals.custom_fee,
      total: totals.total,
      cancellation_policy_notes: this.cancellation_policy_notes ?? null,
    }
  }

  extractAllActivities() {
    // Centralizza filtri e validazione per il salvataggio
    return this.itinerary_days.flatMap(day => day.activities)
      // Filtro attività 'out'
      .filter(activity => activity.inOrOutActivity !== 'out')
      // Valida ogni attività tramite metodo specifico
      .filter(activity => {
        if (typeof activity.validateActivity === 'function') {
          return activity.validateActivity();
        }
        // fallback: base check su id
        return !!activity.id;
      })
      // Rimuovi i campi non necessari per il salvataggio
      .map(activity => {
        // Elimina campi computati/non persistenti
        const { is_multi_day, inOrOutActivity, ...rest } = activity;
        return rest;
      });
  };


  getTotalsPayload(): Record<string, number | string> {
    const t = this.extractTotals();
    return {
      commisionable_total: t.commisionable_total,
      not_commisionable_total: t.not_commisionable_total,
      p2b_commission_percentage: t.p2b_commission_percentage,
      p2b_commission_amount: t.p2b_markup_gross,
      p2b_commission_type: this.p2b_commission_type,
      p2b_commission_fixed_amount: this.p2b_commission_fixed_amount,
      td_fee_percentage: t.td_fee_percentage,
      custom_fee: t.custom_fee,
      total: t.total,
    };
  }

  extractTotals(): ItineraryTotals {
    const allActivities = this.extractAllActivities();
    
    // LOGICA COMMISSIONI (definita in meeting):
    // 1. Totale base = costo servizi (voli + hotel + attività)
    // 2. Markup = fee advisor (% del commissionabile, modificabile)
    // 3. Totale finale = Totale base + Markup (quello che paga il cliente)
    // 4. Place2Be fee = 3% del Totale finale (fee sul "transato")
    // 5. Guadagno advisor = Markup - Place2Be fee
    
    const p2b_commission_percentage = this.p2b_commission_percentage || 20; // Fee advisor (default 20%)
    const td_fee_percentage = 97; // Deprecato, manteniamo per compatibilità DB
    const custom_fee = this.custom_fee || 0; // Sovrapprezzo admin
    const P2B_REVENUE_RATE = 0.03; // Place2Be prende sempre il 3% del totale finale

    const result: ItineraryTotals = {
      commisionable_total: 0,
      not_commisionable_total: 0,
      p2b_commission_percentage,
      td_fee_percentage,
      custom_fee,
      p2b_markup_gross: 0,
      vat_amount: 0,
      p2b_markup_net: 0,
      td_fee_amount: 0,
      p2b_fee_amount: 0,
      total: 0,
    }

    // 1) Somma i costi delle attività (Totale base)
    allActivities.forEach(activity => {
      if (!activity.include_price || !('cost' in activity)) return;
      if (activity.type === 'flight') {
        result.not_commisionable_total += activity.cost;
      } else {
        result.commisionable_total += activity.cost;
      }
    });

    // 2) Calcola Markup advisor: cifra fissa o % del commissionabile
    if (this.p2b_commission_type === 'fixed') {
      result.p2b_markup_gross = this.p2b_commission_fixed_amount || 0;
    } else {
      result.p2b_markup_gross = result.commisionable_total * (p2b_commission_percentage / 100);
    }
    
    // 3) Totale finale = Totale base + Markup (quello che paga il cliente, senza custom fee)
    const totalWithFee = result.commisionable_total + result.not_commisionable_total + result.p2b_markup_gross;
    
    // 4) Place2Be prende il 3% del Totale finale (fee sul "transato")
    result.p2b_fee_amount = totalWithFee * P2B_REVENUE_RATE;
    
    // 5) Advisor riceve: Markup - Place2Be fee
    result.td_fee_amount = result.p2b_markup_gross - result.p2b_fee_amount;
    
    // 6) Campi deprecati (per compatibilità con vecchio sistema)
    result.vat_amount = 0;
    result.p2b_markup_net = result.p2b_markup_gross;
    
    // 7) Totale finale cliente = Totale base + Markup + Custom fee
    result.total = totalWithFee + custom_fee;

    return result;
  };

  // Note: updateTotals removed - totals are calculated on-the-fly via extractTotals()
  // Only percentages (p2b_commission_percentage, td_fee_percentage) and custom_fee are persisted
}

function mergeDays(originalDays: ItineraryDay[], newDays: ItineraryDay[]): ItineraryDay[] {
  if (!originalDays.length) return newDays;
  if (!newDays.length) return originalDays;

  return originalDays.map(activity => {
    const newActivity = newDays.find(a => a.date === activity.date);
    return newActivity || activity;
  });

}

function generateDaysFromActivities(activities: ItineraryActivity[]): ItineraryDay[] {
  const { start_date, end_date } = getStartAndEndDateFromActivities(activities);
  return generateDaysFromDates(start_date, end_date, activities);
}

function generateDaysFromDates(start_date: string, end_date: string, activities: ItineraryActivity[] = []): ItineraryDay[] {

  if (!start_date || !end_date) return [];

  const days: ItineraryDay[] = [];
  const start = new Date(start_date);
  const end = new Date(end_date);


  const endPlusOne = new Date(end);
  endPlusOne.setDate(endPlusOne.getDate() + 1);

  const current = new Date(start);

  while (current < endPlusOne) {
    const currentDateString = dateToLocalString(current);

    const activitiesForDay = activities.filter(activity =>
      activity.date === currentDateString
    );

    days.push(new ItineraryDay({
      date: currentDateString,
      activities: activitiesForDay
    }));

    // Move to next day
    current.setDate(current.getDate() + 1);
  }

  return days;
}

function getStartAndEndDateFromActivities(activities: ItineraryActivity[]): { start_date: string, end_date: string } {
  if (!activities.length) {
    return { start_date: '', end_date: '' };
  }

  let start_date = activities[0].date;
  let end_date = activities[0].date;

  for (let i = 1; i < activities.length; i++) {
    const currentDate = activities[i].date;
    if (currentDate < start_date) {
      start_date = currentDate;
    }
    if (currentDate > end_date) {
      end_date = currentDate;
    }
  }

  return { start_date, end_date };
}